import React from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  AlertIndicator,
  alertTypes,
  ModalButtonsContainer,
  SelectInput,
  showErrorDialog,
  showToast,
  TextButton,
  TextInput,
  useMutationWrapper,
  type SelectOption,
  REQUIRED_ERROR_MESSAGE,
} from "@SeemplicityDev/shared";
import { ACCOUNT_TYPES } from "../../utils";
import { UPDATE_ACCOUNT_TYPE } from "./UpdateAccountTypeApi";
import { ApolloError, type FetchResult } from "@apollo/client";

const validationText = "update";

interface UpdateAccountTypeProps {
  accountName: string;
  accountUuid: string;
  accountType: string;
  closeModal: () => void;
}

interface UpdateAccountTypeFormData {
  accountType: string;
  validationText: string;
}

interface UpdateAccountTypeMutationResult {
  cnc_update_account_type: {
    ok: boolean;
    msg: string;
  };
}

const UpdateAccountType: React.FC<UpdateAccountTypeProps> = ({
  accountName,
  accountUuid,
  accountType,
  closeModal,
}) => {
  const methods = useForm<UpdateAccountTypeFormData>({ mode: "all" });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = methods;

  const [updateAccountType] =
    useMutationWrapper<UpdateAccountTypeMutationResult>(UPDATE_ACCOUNT_TYPE, {
      refetchQueries: ["cnc_organizations"],
    });

  const onSubmit: SubmitHandler<UpdateAccountTypeFormData> = (data) => {
    updateAccountType({
      variables: {
        account_uuid: accountUuid,
        account_type: data.accountType,
      },
    })
      .then((res: FetchResult<UpdateAccountTypeMutationResult>) => {
        if (res.data?.cnc_update_account_type?.ok) {
          closeModal();
          showToast({ message: res.data.cnc_update_account_type.msg });
        } else {
          showErrorDialog({ message: res.data!.cnc_update_account_type.msg });
        }
      })
      .catch((err: ApolloError) => {
        showErrorDialog({ message: err.message });
      });
  };

  const accountTypeOptions = Object.values(ACCOUNT_TYPES).map(
    (type: string) => ({
      label: type,
      value: type,
    })
  );

  return (
    <form
      className="create-org-account-modal"
      onSubmit={handleSubmit(onSubmit)}
    >
      <AlertIndicator
        type={alertTypes.WARNING}
        content={
          <p>
            Current account type is <span className="bold">{accountType}</span>
          </p>
        }
        title={`You are updating the account type for "${accountName}"`}
      />
      <Controller
        name={"accountType"}
        render={({ field: { value, onChange, onBlur } }) => {
          return (
            <SelectInput
              options={accountTypeOptions}
              value={accountTypeOptions.find(
                (type: SelectOption) => type.value === value
              )}
              onChange={(selectedValue: SelectOption | null) => {
                return onChange(selectedValue?.value as string);
              }}
              onBlur={onBlur}
              name={"accountType"}
              labelTop
              label={"Account Type"}
              placeholder={"Select Account Type"}
              error={errors.accountType ?? undefined}
              required
            />
          );
        }}
        control={control}
        rules={{
          required: REQUIRED_ERROR_MESSAGE,
        }}
      />
      <Controller
        name={"validationText"}
        render={({ field: { value, onChange, onBlur } }) => {
          return (
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              name={"validationText"}
              label={
                "To confirm the account type update, please type the following text:"
              }
              labelTop
              placeholder={validationText}
              error={errors.validationText ? errors.validationText : null}
              required
            />
          );
        }}
        control={control}
        rules={{
          required: REQUIRED_ERROR_MESSAGE,
          validate: (value) => {
            return value === validationText || "Validation text is incorrect";
          },
        }}
      />
      <ModalButtonsContainer>
        <TextButton
          type="button"
          data-testid={"update-account-type-cancel"}
          label={"Cancel"}
          onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            closeModal();
          }}
        />
        <TextButton
          type="submit"
          label={"Update Account Type"}
          data-testid={"update-account-type"}
          buttonStyle={"primary"}
          isSubmitting={isSubmitting}
          rightIcon={isSubmitting ? "seem-icon-spinner spinner" : ""}
          disabled={!isValid || !isDirty || isSubmitting}
        />
      </ModalButtonsContainer>
    </form>
  );
};

export default UpdateAccountType;
